/**
 * Factory that builds one of the 5 named panel extensions (info / note /
 * success / warning / error). All 5 share an identical schema except
 * for the node name + `data-block-type` modifier, so a single factory
 * with two parameters covers every case.
 *
 * Schema shape:
 *   - `group: 'block'`            — sits anywhere a block can.
 *   - `content: 'block+'`         — holds one or more block children
 *                                   (paragraphs, lists, headings, hr).
 *                                   NOT panel kinds — see the
 *                                   ProseMirror plugin below that
 *                                   intercepts panel-in-panel inserts.
 *   - `defining: true`            — backspace at the start of the
 *                                   first child unwraps the panel
 *                                   rather than dissolving it.
 *   - `isolating: true`           — selection / cursor ops respect the
 *                                   panel boundary; a Cmd+A inside
 *                                   selects the panel body, not the
 *                                   whole doc.
 *   - `selectable: true`          — the panel itself can become a
 *                                   NodeSelection (Escape from a child
 *                                   surfaces the container).
 *   - `draggable: true`           — the NodeView's left-edge handle
 *                                   gives drag-target affordance.
 *
 * Render contract:
 *   - HTML attribute `data-block-type="<kind>-panel"` is the
 *     parseHTML matcher AND the Playwright selector handle. Stable
 *     across versions.
 *   - The React NodeView uses `NodeViewWrapper` so ProseMirror still
 *     owns the contenteditable child. NodeViewContent renders inside.
 *
 * Why a factory rather than per-file extensions?
 *   The 5 named panels share 100% of the schema; only the node name +
 *   data-block-type differs. A factory removes the duplication and
 *   makes "add a new named panel" a 5-line entry in `panel-kinds.ts`,
 *   not a new file. `customPanel` has distinct attributes (color +
 *   icon) and gets its own factory in `custom-panel-extension.ts`.
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

import { exitIsolatingBlockDown } from '@/editor/lib/exit-isolating-block-down'

import { buildPanelPlaceholderPlugin } from './panel-placeholder-plugin'

export interface BuildPanelExtensionOptions {
  /**
   * ProseMirror node name — `infoPanel`, `notePanel`, etc. Must be
   * unique across the entire schema; the names in `NAMED_PANELS` are
   * the canonical set. Driving the slash-command runs + Playwright
   * assertions; never i18n-translated.
   */
  name: 'infoPanel' | 'notePanel' | 'successPanel' | 'warningPanel' | 'errorPanel'
  /**
   * Kebab-case identifier mirrored to a `data-block-type` HTML
   * attribute. Stable on-wire shape — a saved JSON doc references this
   * via `parseHTML`. Keep aligned with the matching entry in
   * `NAMED_PANELS`.
   */
  dataBlockType: 'info-panel' | 'note-panel' | 'success-panel' | 'warning-panel' | 'error-panel'
  /**
   * React component rendered through ReactNodeViewRenderer. The
   * factory accepts it as a parameter so the per-kind NodeView (with
   * its own icon + stripe shade) can be passed in. `ReactNodeViewProps`
   * is the canonical prop bag the renderer expects — it's a superset
   * of the legacy `NodeViewProps` with an explicit `ref` field that
   * the new TipTap version requires.
   */
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
  /**
   * i18n key suffix used by the empty-panel placeholder plugin —
   * `wiki.panel.placeholder` is the shared key, so this parameter is
   * unused right now; kept on the option object so a future per-kind
   * placeholder (e.g. "Type / for an info-panel command") can land
   * without a factory signature change.
   */
  placeholderKey?: string
}

/**
 * Build the configured Node extension. Returns a fresh extension
 * instance per call — TipTap requires per-editor instance ownership,
 * same constraint as Mention / wikilink / slash-command extensions.
 */
export function buildPanelExtension({
  name,
  dataBlockType,
  view,
  placeholderKey,
}: BuildPanelExtensionOptions) {
  return Node.create({
    name,
    group: 'block',
    content: 'block+',
    defining: true,
    isolating: true,
    selectable: true,
    draggable: true,

    addAttributes() {
      // 2026-05-29 unification: named panels now accept the same
      // `color` + `icon` attrs as customPanel so users can repaint /
      // re-glyph any panel via the in-panel picker. Both attrs are
      // optional + nullable; the NodeView resolves a missing attr by
      // falling back to the kind's `defaultColor` / `defaultIcon`
      // from `NAMED_PANELS` in `panel-kinds.ts`. Backwards-compatible:
      // existing saved docs without these attrs parse to `null` and
      // render with their kind's default visual.
      return {
        color: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => el.getAttribute('data-color'),
          renderHTML: (attrs: { color: string | null }) =>
            attrs.color ? { 'data-color': attrs.color } : {},
        },
        icon: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => el.getAttribute('data-icon'),
          renderHTML: (attrs: { icon: string | null }) =>
            attrs.icon ? { 'data-icon': attrs.icon } : {},
        },
      }
    },

    parseHTML() {
      return [{ tag: `div[data-block-type="${dataBlockType}"]` }]
    },

    renderHTML({ HTMLAttributes }) {
      // Render as a plain `<div>` with the canonical block-type
      // attribute. The 0-hole at the end is where ProseMirror inlines
      // the editable children (`block+` content above).
      return ['div', { ...HTMLAttributes, 'data-block-type': dataBlockType }, 0]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },

    addKeyboardShortcuts() {
      // ArrowDown at the end of the panel's last paragraph exits the
      // panel into a fresh paragraph below (creating one if there's no
      // following sibling). Prevents the ProseMirror gap cursor — which
      // renders as a thin horizontal line — from flashing under the
      // panel, a UX surprise on isolating blocks (panel `isolating:
      // true` means the cursor can't leak past the boundary naturally).
      // Cmd-Enter does the same so users who are mid-typing can exit
      // without needing to find ArrowDown.
      const exit = exitIsolatingBlockDown(name)
      return {
        ArrowDown: ({ editor }) => editor.commands.command(exit),
        'Mod-Enter': ({ editor }) => editor.commands.command(exit),
      }
    },

    addProseMirrorPlugins() {
      // Per-kind placeholder plugin — fires only when the cursor is
      // inside this kind of panel + the first child paragraph is
      // empty. Shared placeholder key for v1 (`wiki.panel.placeholder`);
      // the `placeholderKey` option exists for a future per-kind hint.
      return [
        buildPanelPlaceholderPlugin({
          panelNodeName: name,
          translationKey: placeholderKey ?? 'wiki:panel.placeholder',
        }),
      ]
    },
  })
}
